import React, { useEffect, useMemo, useState } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';
import { db, storage } from '../../firebaseConfig';
import { authenticateAdWallet, callAdMarketplace, executeAdTransaction } from '../../utils/adMarketplaceClient';
import { AD_APPROVAL_MODES, AD_CAMPAIGNS_COLLECTION, AD_MEDIA_TYPES, AD_PLACEMENTS_COLLECTION, buildAdCreativeUploadPath, formatAdMediaLimit, getAdMediaLimitBytes, normalizeAdMedium, formatAdAmount } from '../../utils/adMarketplace';
import '../MuultiStyle.css';

const initialForm = {
    campaignName: '',
    description: '',
    destinationUrl: '',
    medium: 'text',
    creativeTitle: '',
    creativeBody: '',
    payoutModel: 'cpm',
    rate: '5',
    totalBudget: '',
    dailyCap: '',
    perCreatorCap: '',
    currency: 'SOL',
    approvalMode: 'approvalRequired',
    startsAt: '',
    endsAt: '',
    invitedCreators: '',
};

export default function AdComposerPage() {
    const wallet = useWallet();
    const publicKey = wallet?.publicKey;
    const [form, setForm] = useState(initialForm);
    const [creativeFile, setCreativeFile] = useState(null);
    const [campaigns, setCampaigns] = useState([]);
    const [placements, setPlacements] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [payoutAmounts, setPayoutAmounts] = useState({});
    const [fundingReview, setFundingReview] = useState(null);

    const advertiserPublicKey = publicKey?.toBase58?.() || '';
    const selectedMedium = normalizeAdMedium(form.medium);
    const mediaLimit = getAdMediaLimitBytes(selectedMedium);
    const needsFile = ['image', 'video', 'audio'].includes(selectedMedium);
    const acceptsFile = ['image', 'video', 'audio', 'link'].includes(selectedMedium);

    const fileAccept = useMemo(() => {
        if (selectedMedium === 'video') return 'video/*';
        if (selectedMedium === 'audio') return 'audio/*';
        if (selectedMedium === 'image' || selectedMedium === 'link') return 'image/*';
        return '';
    }, [selectedMedium]);

    useEffect(() => {
        setCampaigns([]); setPlacements([]); setFundingReview(null);
        if (!advertiserPublicKey) return undefined;
        const fail = () => setError('Advertiser data could not be loaded.');
        const stopCampaigns = onSnapshot(query(collection(db, AD_CAMPAIGNS_COLLECTION), where('advertiserPublicKey', '==', advertiserPublicKey)), snapshot => setCampaigns(snapshot.docs.map(item => ({ ...item.data(), id: item.id }))), fail);
        const stopPlacements = onSnapshot(query(collection(db, AD_PLACEMENTS_COLLECTION), where('advertiserPublicKey', '==', advertiserPublicKey)), snapshot => setPlacements(snapshot.docs.map(item => ({ ...item.data(), id: item.id }))), fail);
        return () => { stopCampaigns(); stopPlacements(); };
    }, [advertiserPublicKey]);

    const setField = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
        setError('');
        setMessage('');
    };

    const ensureWalletAuth = () => authenticateAdWallet(wallet);

    const uploadCreativeIfNeeded = async (uid) => {
        if (!creativeFile) return { assetUrl: '', assetPath: '' };
        if (mediaLimit && creativeFile.size > mediaLimit) {
            throw new Error(`Creative is too large. Maximum for ${selectedMedium} ads is ${formatAdMediaLimit(selectedMedium)}.`);
        }
        const mime = selectedMedium === 'link' ? 'image/' : selectedMedium + '/';
        if (!creativeFile.type.startsWith(mime)) throw new Error('Creative file type does not match the selected medium.');
        const uploadPath = buildAdCreativeUploadPath({
            medium: selectedMedium,
            uid,
            fileName: creativeFile.name,
        });
        const storageRef = ref(storage, uploadPath);
        await new Promise((resolve, reject) => {
            uploadBytesResumable(storageRef, creativeFile).on('state_changed', null, reject, resolve);
        });
        return { assetUrl: await getDownloadURL(storageRef), assetPath: uploadPath };
    };

    const campaignById = useMemo(() => {
        const map = new Map();
        campaigns.forEach((campaign) => {
            if (campaign?.id) map.set(String(campaign.id), campaign);
        });
        return map;
    }, [campaigns]);

    const handleCreateCampaign = async () => {
        setLoading(true); setError(''); setMessage('');
        try {
            const uid = await ensureWalletAuth();
            if (!form.campaignName.trim()) throw new Error('Campaign name is required.');
            if (needsFile && !creativeFile) throw new Error('This medium requires a creative file.');
            const creative = await uploadCreativeIfNeeded(uid);
            await callAdMarketplace('createCampaign', { campaign: {
                ...form, campaignName: form.campaignName.trim(),
                startsAtMs: form.startsAt ? new Date(form.startsAt).getTime() : 0,
                endsAtMs: form.endsAt ? new Date(form.endsAt).getTime() : 0,
                invitedCreators: form.invitedCreators.split(/[\s,]+/).filter(Boolean),
                creative: { ...creative, title: form.creativeTitle.trim() || form.campaignName.trim(), body: form.creativeBody.trim() },
            } }, wallet);
            setForm(initialForm); setCreativeFile(null);
            setMessage('Draft saved. Review its terms, then fund the escrow to make the offer available.');
        } catch (err) { setError(err?.message || 'Could not create campaign.'); }
        finally { setLoading(false); }
    };
    const handlePlacementStatusChange = async (placement, nextStatus) => {
        setLoading(true); setError(''); setMessage('');
        try {
            await callAdMarketplace('updatePlacement', { placementId: placement.id, status: nextStatus }, wallet);
            setMessage('Placement ' + nextStatus + '.');
        } catch (err) { setError(err?.message || 'Could not update placement.'); }
        finally { setLoading(false); }
    };
    const handleEscrowAction = async (campaign, action, extra = {}) => {
        setLoading(true); setError(''); setMessage('');
        try {
            const prepared = await callAdMarketplace(action, { campaignId: campaign.id, ...extra }, wallet);
            if (action === 'prepareFunding') { setFundingReview({ campaign, prepared }); return; }
            const signature = await executeAdTransaction(wallet, prepared);
            if (action !== 'syncEscrow') await callAdMarketplace('syncEscrow', { campaignId: campaign.id, placementId: extra.placementId, force: true }, wallet);
            setMessage(signature ? 'Escrow transaction finalized: ' + signature : 'Escrow status refreshed.');
        } catch (err) { setError(err?.message || 'Escrow operation failed.'); }
        finally { setLoading(false); }
    };

    const confirmFunding = async () => {
        if (!fundingReview) return;
        setLoading(true); setError('');
        try {
            const prepared = await callAdMarketplace('prepareFunding', { campaignId: fundingReview.campaign.id }, wallet);
            const fields = ['pool', 'programId', 'cluster', 'amountAtomic', 'platformFeeBps', 'termsHash'];
            if (fields.some(key => prepared[key] !== fundingReview.prepared[key])) {
                setFundingReview({ ...fundingReview, prepared });
                throw new Error('Funding terms changed. Review the updated offer before signing.');
            }
            const signature = await executeAdTransaction(wallet, prepared);
            await callAdMarketplace('syncEscrow', { campaignId: fundingReview.campaign.id, force: true }, wallet);
            setFundingReview(null); setMessage('Escrow funding finalized: ' + signature);
        } catch (err) { setError(err?.message || 'Funding failed.'); }
        finally { setLoading(false); }
    };


    return (
        <div className="muulti-container">
            <h1>Ad Composer</h1>
            <p>Create an offer, review its terms, and fund its escrow before it becomes available to creators.</p>

            <div className="new-component-form" style={{ maxWidth: 860, marginInline: 'auto' }}>
                <label>
                    Campaign Name
                    <input
                        className="muulti-input"
                        value={form.campaignName}
                        onChange={(event) => setField('campaignName', event.target.value)}
                    />
                </label>
                <label>
                    Description
                    <textarea
                        className="muulti-textarea"
                        rows={3}
                        value={form.description}
                        onChange={(event) => setField('description', event.target.value)}
                    />
                </label>
                <label>
                    Destination URL
                    <input
                        className="muulti-input"
                        type="url"
                        value={form.destinationUrl}
                        onChange={(event) => setField('destinationUrl', event.target.value)}
                        placeholder="https://example.com"
                    />
                </label>
                <label>
                    Medium
                    <select
                        className="muulti-input"
                        value={form.medium}
                        onChange={(event) => {
                            setCreativeFile(null);
                            setField('medium', event.target.value);
                        }}
                    >
                        {AD_MEDIA_TYPES.map((medium) => (
                            <option key={medium.value} value={medium.value}>
                                {medium.label} ({formatAdMediaLimit(medium.value)})
                            </option>
                        ))}
                    </select>
                </label>
                <label>
                    Creative Title
                    <input
                        className="muulti-input"
                        value={form.creativeTitle}
                        onChange={(event) => setField('creativeTitle', event.target.value)}
                    />
                </label>
                <label>
                    Creative Text
                    <textarea
                        className="muulti-textarea"
                        rows={4}
                        value={form.creativeBody}
                        onChange={(event) => setField('creativeBody', event.target.value)}
                    />
                </label>
                {acceptsFile ? (
                    <label>
                        Creative File {needsFile ? '(required)' : '(optional image)'}
                        <input
                            className="muulti-input"
                            type="file"
                            accept={fileAccept}
                            onChange={(event) => setCreativeFile(event.target.files?.[0] || null)}
                        />
                    </label>
                ) : null}
                <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
                    <label>
                        Payout Model
                        <select
                            className="muulti-input"
                            value={form.payoutModel}
                            onChange={(event) => setField('payoutModel', event.target.value)}
                        >
                            <option value="cpm">CPM</option>
                            <option value="cpv" disabled={!['video', 'audio'].includes(selectedMedium)}>CPV (10 seconds of visible playback)</option>
                        </select>
                    </label>
                    <label>
                        Rate ({form.payoutModel === 'cpm' ? 'per 1,000 impressions' : 'per qualified view'})
                        <input
                            className="muulti-input"
                            type="number"
                            min="0"
                            step="0.000001"
                            value={form.rate}
                            onChange={(event) => setField('rate', event.target.value)}
                        />
                    </label>
                    <label>
                        Total Budget
                        <input
                            className="muulti-input"
                            type="number"
                            min="0"
                            step="0.000001"
                            value={form.totalBudget}
                            onChange={(event) => setField('totalBudget', event.target.value)}
                        />
                    </label>
                    <label>
                        Currency
                        <select
                            className="muulti-input"
                            value={form.currency}
                            onChange={(event) => setField('currency', event.target.value)}
                        >
                            <option value="SOL">SOL</option>
                            <option value="USDC">USDC</option>
                        </select>
                    </label>
                </div>
                <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
                    <label>
                        Daily Approved Spend Cap (including fees)
                        <input className="muulti-input" type="number" min="0" value={form.dailyCap} onChange={(event) => setField('dailyCap', event.target.value)} />
                    </label>
                    <label>
                        Lifetime Per-Creator Cap (including fees)
                        <input className="muulti-input" type="number" min="0" value={form.perCreatorCap} onChange={(event) => setField('perCreatorCap', event.target.value)} />
                    </label>
                    <label>
                        Approval Mode
                        <select className="muulti-input" value={form.approvalMode} onChange={(event) => setField('approvalMode', event.target.value)}>
                            {AD_APPROVAL_MODES.map((mode) => (
                                <option key={mode.value} value={mode.value}>{mode.label}</option>
                            ))}
                        </select>
                    </label>
                </div>
                {form.approvalMode === 'inviteOnly' ? <label>Invited Wallets<textarea className="muulti-textarea" value={form.invitedCreators} onChange={event => setField('invitedCreators', event.target.value)} placeholder="One creator wallet per line" /></label> : null}
                <p>Budgets include platform fees. A cap of 0 means no additional cap. Payouts require your wallet approval; unverified traffic never spends funds automatically. Unallocated funds unlock 24 hours after serving stops. Reserved creator payouts remain claimable.</p>
                <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
                    <label>
                        Start
                        <input className="muulti-input" type="datetime-local" value={form.startsAt} onChange={(event) => setField('startsAt', event.target.value)} />
                    </label>
                    <label>
                        End
                        <input className="muulti-input" type="datetime-local" value={form.endsAt} onChange={(event) => setField('endsAt', event.target.value)} />
                    </label>
                </div>
                {error ? <p className="error">{error}</p> : null}
                {message ? <p>{message}</p> : null}
                <button className="muulti-button" type="button" disabled={loading} onClick={handleCreateCampaign}>
                    {loading ? 'Working...' : 'Save Campaign Draft'}
                </button>
            </div>

            {fundingReview ? <section className="component-item" style={{ maxWidth: 860, margin: '24px auto' }}>
                <h2>Review Escrow Funding</h2>
                <p>{fundingReview.campaign.campaignName}: {formatAdAmount(fundingReview.prepared.amountAtomic, fundingReview.campaign.currency)} {fundingReview.campaign.currency} on {fundingReview.prepared.cluster}, plus account rent and network fees.</p>
                <p>The platform fee is {fundingReview.prepared.platformFeeBps / 100}% of each creator payout, charged from this budget in addition to the creator amount. Funding alone does not approve any payout.</p>
                <button className="muulti-button" disabled={loading} onClick={confirmFunding}>Sign and Fund Escrow</button>
                <button className="muulti-button" disabled={loading} onClick={() => setFundingReview(null)}>Cancel Review</button>
            </section> : null}
            <section style={{ maxWidth: 860, margin: '24px auto 0' }}>
                <h2>Your Campaigns</h2>
                {campaigns.length === 0 ? (
                    <p>No campaigns yet.</p>
                ) : campaigns.map((campaign) => (
                    <div key={campaign.id} className="component-item" style={{ marginBottom: 12 }}>
                        <strong>{campaign.campaignName}</strong>
                        <p>{campaign.medium} - {campaign.payoutModel?.toUpperCase()} {formatAdAmount(campaign.rateAtomic, campaign.currency)} {campaign.currency}</p>
                        <p>Status: {campaign.status} | Escrow: {campaign.escrowStatus || 'unfunded'}</p>
                        {campaign.schemaVersion === 2 ? <>
                            <p>Unallocated: {formatAdAmount(campaign.remainingBudgetAtomic, campaign.currency)} {campaign.currency} | Reserved for creators: {formatAdAmount(campaign.reservedCreatorAtomic, campaign.currency)} | Platform fee: {campaign.platformFeeBps == null ? 'Shown before funding' : (campaign.platformFeeBps / 100) + '% of creator payouts'}</p>
                            <div className="muulti-container-button-container">
                                {campaign.status === 'draft' ? <button className="muulti-button" disabled={loading} onClick={() => handleEscrowAction(campaign, 'prepareFunding')}>Fund Escrow</button> : null}
                                {campaign.termsHash ? <button className="muulti-button" disabled={loading} onClick={() => handleEscrowAction(campaign, 'syncEscrow', { force: true })}>Refresh Escrow</button> : null}
                                {campaign.status === 'active' ? <button className="muulti-button" disabled={loading} onClick={() => handleEscrowAction(campaign, 'setCampaignStatus', { status: 'paused' })}>Pause Campaign</button> : null}
                                {campaign.status === 'paused' ? <button className="muulti-button" disabled={loading} onClick={() => handleEscrowAction(campaign, 'setCampaignStatus', { status: 'active' })}>Resume Campaign</button> : null}
                                {(campaign.status !== 'ended' || (campaign.termsHash && campaign.chainStatus !== 'ended')) ? <button className="muulti-button" disabled={loading} onClick={() => handleEscrowAction(campaign, 'setCampaignStatus', { status: 'ended' })}>End Campaign</button> : null}
                                {campaign.termsHash ? <button className="muulti-button" disabled={loading} onClick={() => handleEscrowAction(campaign, 'prepareWithdrawal')}>Withdraw Unallocated Funds</button> : null}
                            </div>
                        </> : <p>Create a new offer to use protected escrow and approvals.</p>}
                    </div>
                ))}
            </section>

            <section style={{ maxWidth: 860, margin: '24px auto 0' }}>
                <h2>Creator Placement Requests</h2>
                {!advertiserPublicKey ? (
                    <p>Connect your wallet to review creator requests.</p>
                ) : placements.length === 0 ? (
                    <p>No creator placement requests yet.</p>
                ) : placements.map((placement) => {
                    const campaign = campaignById.get(String(placement.campaignId));
                    const status = String(placement.status || 'pending');
                    return (
                        <div key={placement.id} className="component-item" style={{ marginBottom: 12 }}>
                            <strong>{campaign?.campaignName || placement.campaignId}</strong>
                            <p>
                                {placement.spaceUsername || placement.creatorPublicKey} - {placement.componentTitle || 'Ad Panel'}
                            </p>
                            <p>
                                Status: {status} | {placement.payoutModel?.toUpperCase() || campaign?.payoutModel?.toUpperCase() || 'CPM'} {formatAdAmount(placement.rateAtomic, placement.currency)} {placement.currency || campaign?.currency || 'SOL'}
                            </p>
                            <p>Unverified activity: {placement.rawImpressions || 0} impressions, {placement.rawViews || 0} qualified views. These counts are not a verified bill.</p>
                            {campaign?.schemaVersion === 2 && ['approved', 'active', 'paused', 'ended'].includes(status) ? <div>
                                <p>Platform fees of {campaign.platformFeeBps / 100}% are added to this creator amount and charged from the campaign budget.</p>
                                <label>Creator Payout ({placement.currency})<input className="muulti-input" type="number" min="0" step="0.000001" value={payoutAmounts[placement.id] || ''} onChange={event => setPayoutAmounts(previous => ({ ...previous, [placement.id]: event.target.value }))} /></label>
                                <button className="muulti-button" disabled={loading || !payoutAmounts[placement.id]} onClick={() => handleEscrowAction(campaign, 'prepareSettlement', { placementId: placement.id, amount: payoutAmounts[placement.id] })}>Approve Payout in Wallet</button>
                            </div> : null}
                            <div className="muulti-container-button-container">
                                {placement.schemaVersion === 2 && ['pending', 'paused'].includes(status) ? (
                                    <button
                                        className="muulti-button"
                                        type="button"
                                        disabled={loading}
                                        onClick={() => handlePlacementStatusChange(placement, 'approved')}
                                    >
                                        Approve
                                    </button>
                                ) : null}
                                {status === 'pending' ? (
                                    <button
                                        className="muulti-button"
                                        type="button"
                                        disabled={loading}
                                        onClick={() => handlePlacementStatusChange(placement, 'rejected')}
                                    >
                                        Reject
                                    </button>
                                ) : null}
                                {status === 'approved' || status === 'active' ? (
                                    <button
                                        className="muulti-button"
                                        type="button"
                                        disabled={loading}
                                        onClick={() => handlePlacementStatusChange(placement, 'paused')}
                                    >
                                        Pause
                                    </button>
                                ) : null}
                            </div>
                        </div>
                    );
                })}
            </section>
        </div>
    );
}
